import httpClient from '../utils/httpClient';
import { CreateRegulationRequest, Regulation } from '../types/regulation';

const API_BASE = '/api/regulations';

class RegulationsService {
  static async getCategories(): Promise<string[]> {
    const response = await httpClient.get(`${API_BASE}/categories`);
    return response.json();
  }

  static async getTags(): Promise<string[]> {
    const response = await httpClient.get(`${API_BASE}/tags`);
    return response.json();
  }

  static async getRegulation(id: number): Promise<{ regulation: Regulation }> {
    const response = await httpClient.get(`${API_BASE}/${id}`);
    return response.json();
  }

  static async createRegulation(data: CreateRegulationRequest): Promise<Regulation> {
    const response = await httpClient.post(`${API_BASE}`, data);
    return response.json();
  }

  static async updateRegulation(id: string, data: Partial<CreateRegulationRequest> & { version?: number }): Promise<Regulation> {
    const response = await httpClient.put(`${API_BASE}/${id}`, data);
    return response.json();
  }

  static async deleteRegulation(id: number): Promise<void> {
    await httpClient.delete(`${API_BASE}/${id}`);
  }

  static async activateRegulation(id: number): Promise<Regulation> {
    const response = await httpClient.post(`${API_BASE}/${id}/activate`);
    return response.json();
  }

  static async suspendRegulation(id: number): Promise<Regulation> {
    const response = await httpClient.post(`${API_BASE}/${id}/suspend`);
    return response.json();
  }

  static async notifyHotels(id: number): Promise<void> {
    await httpClient.post(`${API_BASE}/${id}/notify`);
  }

  static async getHistory(id: number): Promise<any[]> {
    const response = await httpClient.get(`${API_BASE}/${id}/history`);
    return response.json();
  }

  static getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      active: 'Active',
      suspended: 'Suspendue',
      expired: 'Expirée',
      archived: 'Archivée',
    };
    return labels[status] || status;
  }

  static getPriorityLabel(priority: string): string {
    const labels: Record<string, string> = {
      low: 'Basse',
      medium: 'Moyenne',
      high: 'Haute',
      critical: 'Critique',
    };
    return labels[priority] || priority;
  }

  static getScopeLabel(scope: { type: string; value: string }): string {
    if (!scope) return 'Non défini';
    const types: Record<string, string> = {
      national: 'National',
      regional: 'Régional',
      provincial: 'Provincial',
      local: 'Local',
    };
    return `${types[scope.type] || scope.type}${scope.value ? ` - ${scope.value}` : ''}`;
  }

  static validateRegulation(data: Partial<CreateRegulationRequest>): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!data.title?.trim()) errors.title = 'Le titre est requis';
    if (!data.description?.trim()) errors.description = 'La description est requise';
    if (!data.content?.trim()) errors.content = 'Le contenu est requis';
    if (!data.category?.trim()) errors.category = 'La catégorie est requise';
    if (!data.effective_date) errors.effective_date = 'La date d\'entrée en vigueur est requise';
    return errors;
  }
}

export default RegulationsService;
